import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Trash2, ChevronRight, RefreshCw } from "lucide-react";
import type { NotificationSettings } from "@/hooks/useNotificationSettings";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface SlackConfigProps {
  settings: NotificationSettings;
  onSave: (updates: Partial<NotificationSettings>) => Promise<void>;
}

export function SlackConfig({ settings, onSave }: SlackConfigProps) {
  const [enabled, setEnabled] = useState(settings.slack_enabled);
  const [testing, setTesting] = useState(false);

  // OAuth States
  const [channels, setChannels] = useState<any[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(settings.slack_channel_id || "");

  useEffect(() => {
    setEnabled(settings.slack_enabled);
    setSelectedChannel(settings.slack_channel_id || "");
    if (settings.slack_workspace_name && settings.slack_oauth_access_token) {
      fetchChannels();
    }
  }, [settings]);

  const fetchChannels = async () => {
    setLoadingChannels(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const workerUrl = import.meta.env.VITE_WORKER_URL || "https://uptimewing-worker.consenses.workers.dev";
      const res = await fetch(`${workerUrl}/api/slack/channels`, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });
      if (!res.ok) throw new Error("Failed to load channels");
      const list = await res.json();
      setChannels(list);
    } catch (err: any) {
      console.error(err);
      toast.error("Failed to load Slack channels");
    } finally {
      setLoadingChannels(false);
    }
  };

  const handleOAuthConnect = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast.error("You must be logged in to connect Slack.");
      return;
    }

    const workerUrl = import.meta.env.VITE_WORKER_URL || "https://uptimewing-worker.consenses.workers.dev";
    window.open(
      `${workerUrl}/api/slack/connect?token=${encodeURIComponent(session.access_token)}`,
      "UptimeWingSlackOAuth",
      "width=600,height=700"
    );

    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === "SLACK_OAUTH_SUCCESS") {
        toast.success("Connected to Slack workspace!");
        window.removeEventListener("message", handleMessage);
        window.location.reload();
      }
    };
    window.addEventListener("message", handleMessage);
  };

  const handleDisconnectOAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const workerUrl = import.meta.env.VITE_WORKER_URL || "https://uptimewing-worker.consenses.workers.dev";
      const res = await fetch(`${workerUrl}/api/slack/disconnect`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });
      if (!res.ok) throw new Error("Failed to disconnect");
      toast.success("Slack workspace disconnected successfully.");
      window.location.reload();
    } catch (err: any) {
      toast.error("Failed to disconnect Slack workspace.");
    }
  };

  const handleTestOAuth = async () => {
    setTesting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const workerUrl = import.meta.env.VITE_WORKER_URL || "https://uptimewing-worker.consenses.workers.dev";
      const res = await fetch(`${workerUrl}/api/slack/test`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || "Failed to send test message");
      }
      toast.success("Test notification posted to Slack!");
    } catch (err: any) {
      toast.error(err.message || "Failed to trigger Slack test alert.");
    } finally {
      setTesting(false);
    }
  };

  const handleChannelChange = async (channelId: string) => {
    const channelName = channels.find(c => c.id === channelId)?.name || "";
    setSelectedChannel(channelId);
    try {
      await onSave({ slack_channel_id: channelId, slack_channel_name: channelName });
      toast.success(`Alert destination updated to #${channelName}`);
    } catch (err) {
      toast.error("Failed to update Slack channel");
    }
  };

  const handleSaveWebhook = async () => {
    if (!enabled && !settings.email_enabled && !settings.telegram_enabled && !settings.discord_enabled) {
      toast.error("Security Override: At least one notification channel must be active.");
      setEnabled(true);
      return;
    }
    try {
      await onSave({ slack_webhook_url: url || null, slack_enabled: enabled });
      toast.success("Slack settings saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save Slack settings");
    }
  };

  const handleDisconnectWebhook = async () => {
    try {
      await onSave({ slack_webhook_url: null, slack_enabled: false });
      setUrl("");
      setVerified(false);
      toast.success("Slack credentials purged.");
    } catch (err: any) {
      toast.error("Failed to disconnect.");
    }
  };

  const handleTestWebhook = async () => {
    if (!url) return toast.error("Enter a webhook URL first");
    setTesting(true);
    setVerified(false);
    try {
      const { data, error } = await supabase.functions.invoke("test-integration", {
        body: { type: "slack", url },
      });
      if (error || (data && data.error)) {
        toast.error(error?.message || data?.error || "Failed to send test message");
      } else {
        setVerified(true);
        toast.success("Test message sent to Slack");
      }
    } catch {
      toast.error("Failed to reach Slack webhook");
    } finally {
      setTesting(false);
    }
  };

  const isOAuthConnected = !!settings.slack_workspace_name;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-black uppercase tracking-tight">Slack Integration</p>
          <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Send alerts to your Slack workspace</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} />
      </div>

      <div className="border-2 border-primary/20 bg-primary/5 p-6 rounded-none space-y-3 group hover:border-primary transition-colors flex flex-col justify-between">
        {!isOAuthConnected ? (
          <>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                <span className="text-[10px] font-black uppercase">Slack OAuth bot install (Recommended)</span>
              </div>
              <p className="text-xs font-medium leading-relaxed uppercase tracking-tight text-muted-foreground">
                Add the UptimeWing Slack app to your workspace in one click. Alerts will stream directly to your chosen channel.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleOAuthConnect}
              className="w-full h-10 border-2 border-foreground bg-primary text-primary-foreground hover:bg-foreground hover:text-background font-black uppercase text-[10px] shadow-brutal-sm mt-4"
            >
              <ChevronRight className="h-4 w-4 mr-1.5" /> Add to Slack
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-status-up" />
                <span className="text-[10px] font-black uppercase text-status-up">Connected to {settings.slack_workspace_name}</span>
              </div>
              <p className="text-[9px] text-muted-foreground font-bold uppercase tracking-wider">
                Slack workspace authorized successfully.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-black uppercase opacity-60 flex justify-between">
                <span>Alert Target Channel</span>
                {loadingChannels ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <button onClick={fetchChannels} className="hover:text-foreground flex items-center gap-1">
                    <RefreshCw className="h-2.5 w-2.5" /> Refresh
                  </button>
                )}
              </Label>
              <select
                value={selectedChannel}
                onChange={(e) => handleChannelChange(e.target.value)}
                className="w-full h-10 rounded-none border-2 border-foreground bg-background px-3 font-mono text-[10px] shadow-brutal-sm outline-none focus:border-primary"
              >
                <option value="">-- Select a Channel --</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.name} {c.is_private ? "(Private)" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleTestOAuth}
                disabled={testing || !selectedChannel}
                className="h-8 border-2 border-foreground text-[9px] font-black uppercase shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none"
              >
                {testing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Test Alert
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-8 text-[9px] font-black uppercase text-status-down hover:bg-status-down/10"
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Disconnect
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="glass-strong rounded-none border-4 border-foreground bg-background shadow-brutal p-6">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-base font-black uppercase tracking-tight">Disconnect Workspace</AlertDialogTitle>
                    <AlertDialogDescription className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-2">
                      Are you sure you want to disconnect UptimeWing from {settings.slack_workspace_name}?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="mt-4 gap-2 flex-row justify-end">
                    <AlertDialogCancel className="rounded-none border-2 border-foreground bg-background text-[10px] font-black uppercase tracking-widest shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all mt-0">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDisconnectOAuth}
                      className="rounded-none border-2 border-foreground bg-status-down text-white hover:bg-foreground hover:text-background font-black uppercase text-[10px] shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
                    >
                      Disconnect
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
